/**
 * A tag list as text somebody can rearrange, with the same `#tag` menu over it.
 *
 * ── Why this is not [TagInputField] ──────────────────────────────────────────
 *
 * The settings window had that control, chips and all, and chips turned out to be the wrong
 * shape for what these two lists are. A chip can be added and it can be removed; it cannot
 * be **moved**. That was a fair trade while the order meant nothing — and it stopped being
 * fair the moment the list window's tag rail started drawing *Offer by default* in the order
 * it is given (see `BookmarkListBackingViewModel.rail`). An account that wants `#read-later`
 * first has to be able to put it first, and with chips the only answer was to rename it to
 * something earlier in the alphabet.
 *
 * So here the text **is** the list: `#inbox #read-later #rust`, edited like any other line of
 * text, which makes reordering the ordinary act of moving a word. Everything a chip was doing
 * — showing what is committed, telling one tag from the next — a space already does.
 *
 * The two controls are kept apart rather than merged because their contracts are opposites,
 * and merging them would mean a flag that decides which of two truths the field holds.
 * [TagInputField] holds no set at all: the host owns it, and the field reports requests
 * against it. That seam is exactly what the list window's rail and search need, and its file
 * header argues it at length. This field's set is its own text, and there is nothing for a
 * host to own — which is why the whole of its output is one list, once, on [onEdited].
 *
 * What they genuinely share is the menu, and that is shared as code: [TagMenuPopover], and
 * the pure rules in `TagInput.kt` that decide which rows exist and what a key does.
 *
 * ── When it reports ──────────────────────────────────────────────────────────
 *
 * On blur, and on `⏎` where the menu is not open. **Not on every keystroke**, and that is
 * the one thing this field has to get right: the settings window has no Save button and
 * writes on every intent, so a field that raised one per character would put a `PUT` on the
 * wire for `#r`, `#ru`, `#rus`, `#rust` — each of them a tag list the account never meant to
 * have, the first three of them landing in the popup and the rail on the way past.
 *
 * Blur is the same moment [TagInputRules] settled on for the fields that file: moving away is
 * somebody saying they are done. `⏎` is here as well because it is what a person types after
 * finishing a line, and a field that ignored it would feel broken even though blur would
 * eventually catch it.
 */
package se.soderbjorn.lunapin.web

import kotlinx.browser.document
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.KeyboardEvent
import se.soderbjorn.lunapin.clientserver.TagUse

/**
 * A field holding a whole tag list as text.
 *
 * @param placeholder what the empty field says.
 * @param onEdited the list as it now reads, normalised and in the order it is written.
 *   Raised when the person is done rather than as they type — see the file header.
 */
class TagListField(
    placeholder: String = "",
    private val onEdited: (List<String>) -> Unit = {},
) {
    /** What to mount. */
    val root: HTMLElement = element("div", "lp-taglist")

    private val input: HTMLInputElement = (document.createElement("input") as HTMLInputElement).apply {
        type = "text"
        className = "lp-taglist-input"
        this.placeholder = placeholder
        // A hand-written combobox, for the reason `TagInputRules` gives: a `<datalist>`
        // matches the input's whole value and stops matching after the first tag.
        setAttribute("role", "combobox")
        setAttribute("aria-autocomplete", "list")
        setAttribute("aria-expanded", "false")
        autocomplete = "off"
    }

    private val popover = TagMenuPopover(input) { index -> takeRow(index) }

    private var known: List<TagUse> = emptyList()

    init {
        root.appendChild(input)
        root.appendChild(popover.element)

        input.addEventListener("input", { refreshMenu() })
        input.addEventListener("focus", { refreshMenu() })
        input.addEventListener("blur", { popover.close(); report() })
        input.addEventListener("keydown", { event -> onKeyDown(event as KeyboardEvent) })
    }

    /** The account's tags, from `GET /api/tags`. Safe to call again when they change. */
    fun setKnownTags(tags: List<TagUse>) {
        known = tags
        if (popover.isOpen) refreshMenu()
    }

    /**
     * Paint [tags] as the field's text.
     *
     * **Skipped while the field has focus**, which is not an optimisation but the rule that
     * keeps this usable: the settings window republishes its whole state after every write,
     * including the write this field just caused, and a field that took that repaint would
     * yank the caret to the end mid-word every time the server answered.
     */
    fun setTags(tags: List<String>) {
        if (document.activeElement == input) return
        val text = tags.joinToString(" ")
        if (input.value != text) input.value = text
    }

    /** The `<input>` itself, so a host can ask whether the caret is in it. */
    val textInput: HTMLInputElement get() = input

    /** Rebuild the menu from what is under the caret, or close it if nothing is. */
    private fun refreshMenu() {
        val fragment = tagFragmentAt(input.value, input.selectionStart ?: input.value.length)
        if (fragment == null) {
            popover.close()
            return
        }
        popover.open(
            tagMenu(
                fragment = fragment.text,
                known = known,
                // Everything else already in the line, so the menu stops offering a tag this
                // list holds — the same rule the chip field follows, reading the text instead
                // of a set. The fragment being typed is excluded from that reckoning, or a
                // tag would stop offering itself halfway through being spelled.
                committed = tagsOutside(fragment).toSet(),
                offerCreate = true,
            ),
        )
    }

    /** The tags in the line other than the one under the caret. */
    private fun tagsOutside(fragment: TagFragment): List<String> =
        tagsLeftInField(input.value.removeRange(fragment.start, fragment.end))

    /** Put the highlighted row's tag into the line, in place of what was being typed. */
    private fun takeRow(index: Int) {
        popover.moveHighlight(index)
        applyEnter()
    }

    private fun onKeyDown(event: KeyboardEvent) {
        val outcome = tagKeyOutcome(
            key = event.key,
            altKey = event.altKey,
            value = input.value,
            caret = input.selectionStart ?: input.value.length,
            menu = popover.rows,
            highlighted = popover.highlighted,
            // A tag here is a thing to file under, never a filter, so there is no *not this
            // one* to express. `⌥⏎` commits plainly, which is what `TagInputRules` asks for.
            allowExclude = false,
            // No chips beside the text to back into: the text **is** the list here, so `⌫`
            // can only ever mean ordinary deleting and must reach the browser untouched.
            hasCommitted = false,
        )
        when (outcome) {
            TagKeyOutcome.Ignored -> {
                // `⏎` on a line with nothing half-typed in it is somebody saying they are
                // done — the same moment blur means, arrived at from the keyboard. It is
                // consumed so it does not reach a surrounding form; there is no form on the
                // settings window, and there is no second meaning for it here either.
                if (event.key == "Enter") {
                    event.consume()
                    report()
                }
                return
            }

            // Cannot arrive: `hasCommitted` is false above, because this field has no chips.
            // Named rather than folded into an `else` so that a fourth outcome one day has to
            // be answered here rather than silently swallowed.
            TagKeyOutcome.RemoveLast -> return

            TagKeyOutcome.DismissMenu -> {
                event.consume()
                popover.close()
            }

            is TagKeyOutcome.MoveHighlight -> {
                event.consume()
                popover.moveHighlight(outcome.to)
            }

            is TagKeyOutcome.Commit -> {
                event.consume()
                apply(outcome.tag)
            }
        }
    }

    /** Re-run the `⏎` rule against the current state, for a row that was clicked. */
    private fun applyEnter() {
        val outcome = tagKeyOutcome(
            key = "Enter",
            altKey = false,
            value = input.value,
            caret = input.selectionStart ?: input.value.length,
            menu = popover.rows,
            highlighted = popover.highlighted,
            allowExclude = false,
            hasCommitted = false,
        )
        if (outcome is TagKeyOutcome.Commit) apply(outcome.tag)
    }

    /**
     * Put [tag] into the line where the fragment was, and leave the caret after it.
     *
     * The one place this field parts company with [TagInputField]'s handling of the same
     * outcome: there a committed tag *leaves* the text and becomes a chip, so the text
     * shrinks. Here the text is the list, so the tag stays — spelled out in full, with a
     * trailing space so the next one can be typed straight away.
     */
    private fun apply(tag: String) {
        val fragment = tagFragmentAt(input.value, input.selectionStart ?: input.value.length) ?: return
        val before = input.value.take(fragment.start)
        val after = input.value.substring(fragment.end)
        val insert = "$tag "
        input.value = before + insert + after
        val caret = before.length + insert.length
        input.selectionStart = caret
        input.selectionEnd = caret
        popover.close()
    }

    /**
     * Hand the host the list as it now reads.
     *
     * Normalised through the same [tagsLeftInField] the chip field uses when somebody leaves
     * it, so a line typed as `rust, Web` and a line typed as `#rust #web` are the same list —
     * and de-duplicated in place, so repeating a tag does not make the field disagree with
     * what it is about to be told it holds.
     */
    private fun report() {
        onEdited(tagsLeftInField(input.value))
    }

    /** See `TagInputField.consume`, which explains why both halves are needed. */
    private fun KeyboardEvent.consume() {
        preventDefault()
        stopPropagation()
    }
}
